import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Button, Card, Checkbox, Form, Input, InputNumber, Progress, Select, Space, Table, Tabs, Tag, Typography,
    message,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import adminAudioClient from '../../client/AdminAudioClient';
import useTranslation from '../../hooks/useTranslation';

const { Text } = Typography;

const STATUS_COLOR = {
    PLANNING: 'default', QUEUED: 'default', RUNNING: 'processing', PAUSED: 'warning', PAUSED_BUDGET: 'warning',
    DONE: 'success', FAILED: 'error', CANCELLED: 'default',
};

const KINDS = ['WORD', 'DEFINITION', 'EXAMPLE'];

/**
 * Процент готовности: красный, пока слова не на 100% — слова обязаны быть озвучены
 * целиком, примеры догоняются по мере бюджета.
 */
const CoverageBar = ({ counts, strict }) => {
    if (!counts) {
        return null;
    }
    const percent = counts.percentReady ?? 0;
    const status = strict && percent < 100 ? 'exception' : percent === 100 ? 'success' : 'normal';
    return (
        <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <Progress percent={percent} size="small" status={status} />
            <Text type="secondary" style={{ fontSize: 12 }}>
                {counts.ready}/{counts.total}
                {counts.stale ? ` · stale ${counts.stale}` : ''}
                {counts.failed ? ` · failed ${counts.failed}` : ''}
            </Text>
        </Space>
    );
};

const jobProgress = (job) => {
    const progress = job.progress || {};
    const total = Object.values(progress).reduce((sum, value) => sum + (value || 0), 0);
    const done = (progress.DONE || 0) + (progress.SKIPPED || 0);
    return { total, done, failed: progress.FAILED || 0, percent: total ? Math.floor((100 * done) / total) : 100 };
};

const AdminAudioPage = () => {
    const { t } = useTranslation();
    const [forbidden, setForbidden] = useState(false);
    const [coverage, setCoverage] = useState([]);
    const [coverageLoading, setCoverageLoading] = useState(false);
    const [jobs, setJobs] = useState([]);
    const [jobsLoading, setJobsLoading] = useState(false);
    const [usage, setUsage] = useState([]);
    const [limits, setLimits] = useState({});
    const [estimate, setEstimate] = useState(null);
    const [jobDetails, setJobDetails] = useState({});
    const [problems, setProblems] = useState({});
    const [form] = Form.useForm();

    const handleError = useCallback((error) => {
        if (error?.status === 403) {
            setForbidden(true);
            return;
        }
        console.error('Admin audio request failed:', error);
        message.error(error?.message || t('admin.audio.requestFailed'));
    }, [t]);

    const loadCoverage = useCallback(async () => {
        setCoverageLoading(true);
        try {
            setCoverage(await adminAudioClient.getCoverage());
        } catch (error) {
            handleError(error);
        } finally {
            setCoverageLoading(false);
        }
    }, [handleError]);

    const loadJobs = useCallback(async () => {
        setJobsLoading(true);
        try {
            setJobs(await adminAudioClient.listJobs());
        } catch (error) {
            handleError(error);
        } finally {
            setJobsLoading(false);
        }
    }, [handleError]);

    const loadUsage = useCallback(async () => {
        try {
            const [rows, configured] = await Promise.all([adminAudioClient.getUsage(), adminAudioClient.getLimits()]);
            setUsage(rows);
            setLimits(configured);
        } catch (error) {
            handleError(error);
        }
    }, [handleError]);

    useEffect(() => {
        loadCoverage();
        loadJobs();
        loadUsage();
    }, [loadCoverage, loadJobs, loadUsage]);

    // Пока есть идущее или ждущее раннера задание, список обновляется сам: прогресс —
    // главное, ради чего сюда приходят, а ждущее возьмут на ближайшем тике.
    const hasRunning = useMemo(() => jobs.some((job) => ['RUNNING', 'QUEUED', 'PLANNING'].includes(job.status)), [jobs]);
    useEffect(() => {
        if (!hasRunning) {
            return undefined;
        }
        const timer = setInterval(() => {
            loadJobs();
            loadCoverage();
        }, 10000);
        return () => clearInterval(timer);
    }, [hasRunning, loadJobs, loadCoverage]);

    const enqueuePreset = async (row, scope) => {
        try {
            await adminAudioClient.createJob({
                name: `${row.presetName} ${scope.join('+')}`,
                targetType: 'PRESET',
                presetIds: [row.presetId],
                scope,
                priority: scope.includes('WORD') ? 10 : 0,
                trigger: 'NOW',
            });
            message.success(t('admin.audio.jobCreated'));
            loadJobs();
        } catch (error) {
            handleError(error);
        }
    };

    const audit = async (row) => {
        try {
            await adminAudioClient.auditPreset(row.presetId, KINDS);
            message.info(t('admin.audio.auditStarted'));
        } catch (error) {
            handleError(error);
        }
    };

    const recompute = async (row) => {
        try {
            await adminAudioClient.recomputeCoverage(row.presetId);
            loadCoverage();
        } catch (error) {
            handleError(error);
        }
    };

    // Список заданий не возит неудавшиеся элементы: они запрашиваются при раскрытии строки.
    const loadJobDetails = async (job) => {
        try {
            const details = await adminAudioClient.getJob(job.id);
            setJobDetails((current) => ({ ...current, [job.id]: details }));
        } catch (error) {
            handleError(error);
        }
    };

    const loadProblems = async (row) => {
        try {
            const found = await adminAudioClient.getPresetProblems(row.presetId);
            setProblems((current) => ({ ...current, [row.presetId]: found }));
        } catch (error) {
            handleError(error);
        }
    };

    const jobAction = async (action, job) => {
        try {
            await action(job.id);
            loadJobs();
        } catch (error) {
            handleError(error);
        }
    };

    const toRequest = (values) => ({
        name: values.name,
        targetType: values.targetType,
        presetIds: values.presetIds,
        dictionaryWordIds: values.dictionaryWordIds
            ? values.dictionaryWordIds.split(/[\s,]+/).filter(Boolean) : undefined,
        locale: values.locale || undefined,
        scope: values.scope,
        voice: values.voiceProvider ? {
            provider: values.voiceProvider,
            engine: values.voiceEngine,
            voice: values.voiceName,
            languageCode: values.voiceLanguage,
        } : undefined,
        priority: values.priority || 0,
        trigger: values.trigger,
        cron: values.cron || undefined,
        // Бюджет без класса голоса сервер молча отбросил бы: без класса его не отправляем.
        budgets: values.budgetMaxChars && values.budgetVoiceClass ? [{
            provider: (values.voiceProvider || 'aws').toLowerCase(),
            voiceClass: values.budgetVoiceClass,
            maxChars: values.budgetMaxChars,
        }] : undefined,
    });

    const runEstimate = async () => {
        try {
            const values = await form.validateFields();
            setEstimate(await adminAudioClient.estimateJob(toRequest(values)));
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            handleError(error);
        }
    };

    const submitJob = async () => {
        try {
            const values = await form.validateFields();
            await adminAudioClient.createJob(toRequest(values));
            message.success(t('admin.audio.jobCreated'));
            setEstimate(null);
            form.resetFields();
            loadJobs();
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            handleError(error);
        }
    };

    if (forbidden) {
        return <Alert type="warning" showIcon message={t('admin.audio.forbidden')} />;
    }

    const coverageColumns = [
        { title: t('admin.audio.preset'), dataIndex: 'presetName', key: 'name',
            render: (name, row) => <Space direction="vertical" size={0}><Text strong>{name}</Text><Text type="secondary">{row.locale}</Text></Space> },
        { title: t('admin.audio.words'), key: 'words', width: 180, render: (_, row) => <CoverageBar counts={row.words} strict /> },
        { title: t('admin.audio.definitions'), key: 'definitions', width: 180, render: (_, row) => <CoverageBar counts={row.definitions} /> },
        { title: t('admin.audio.examples'), key: 'examples', width: 180, render: (_, row) => <CoverageBar counts={row.examples} /> },
        { title: '', key: 'actions', render: (_, row) => (
            <Space wrap>
                <Button size="small" onClick={() => enqueuePreset(row, ['WORD'])}>{t('admin.audio.enqueueWords')}</Button>
                <Button size="small" onClick={() => enqueuePreset(row, ['DEFINITION', 'EXAMPLE'])}>{t('admin.audio.enqueueExamples')}</Button>
                <Button size="small" onClick={() => audit(row)}>{t('admin.audio.audit')}</Button>
                <Button size="small" icon={<ReloadOutlined />} onClick={() => recompute(row)} aria-label={t('admin.audio.recompute')} />
            </Space>
        ) },
    ];

    const jobColumns = [
        { title: t('admin.audio.job'), dataIndex: 'name', key: 'name',
            render: (name, job) => <Space direction="vertical" size={0}><Text strong>{name}</Text>
                <Text type="secondary">{job.targetType} · {(job.scope || []).join(', ')} · {job.trigger}{job.voiceOverride ? ` · ${job.voiceOverride.provider}/${job.voiceOverride.voice}` : ''}</Text></Space> },
        { title: t('admin.audio.status'), dataIndex: 'status', key: 'status', width: 140,
            render: (status) => <Tag color={STATUS_COLOR[status] || 'default'}>{status}</Tag> },
        { title: t('admin.audio.progress'), key: 'progress', width: 220, render: (_, job) => {
            const progress = jobProgress(job);
            return (
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                    <Progress percent={progress.percent} size="small" status={progress.failed ? 'exception' : undefined} />
                    <Text type="secondary" style={{ fontSize: 12 }}>{progress.done}/{progress.total}{progress.failed ? ` · failed ${progress.failed}` : ''}</Text>
                </Space>
            );
        } },
        { title: t('admin.audio.priority'), dataIndex: 'priority', key: 'priority', width: 90 },
        { title: '', key: 'actions', render: (_, job) => (
            <Space wrap>
                {['QUEUED', 'RUNNING', 'PAUSED_BUDGET'].includes(job.status) && (
                    <Button size="small" onClick={() => jobAction(adminAudioClient.pauseJob, job)}>{t('admin.audio.pause')}</Button>)}
                {['PAUSED', 'PAUSED_BUDGET'].includes(job.status) && (
                    <Button size="small" onClick={() => jobAction(adminAudioClient.resumeJob, job)}>{t('admin.audio.resume')}</Button>)}
                {['QUEUED', 'RUNNING', 'PAUSED', 'PAUSED_BUDGET'].includes(job.status) && (
                    <>
                        <Button size="small" type="primary" onClick={() => jobAction(adminAudioClient.runJobNow, job)}>{t('admin.audio.runNow')}</Button>
                        <Button size="small" danger onClick={() => jobAction(adminAudioClient.cancelJob, job)}>{t('admin.audio.cancel')}</Button>
                    </>)}
            </Space>
        ) },
    ];

    const usageColumns = [
        { title: t('admin.audio.provider'), dataIndex: 'provider', key: 'provider' },
        { title: t('admin.audio.voiceClass'), dataIndex: 'voiceClass', key: 'voiceClass' },
        { title: t('admin.audio.period'), dataIndex: 'period', key: 'period' },
        { title: t('admin.audio.chars'), dataIndex: 'chars', key: 'chars', render: (chars, row) => (
            row.freeChars ? `${chars.toLocaleString()} / ${row.freeChars.toLocaleString()}` : chars.toLocaleString()) },
        { title: t('admin.audio.requests'), dataIndex: 'requests', key: 'requests' },
    ];

    const presetOptions = coverage.map((row) => ({ value: row.presetId, label: `${row.presetName} (${row.locale})` }));
    const limitRows = Object.entries(limits).flatMap(([provider, classes]) =>
        Object.entries(classes || {}).map(([voiceClass, free]) => ({ provider, voiceClass, free })));

    const tabs = [
        { key: 'coverage', label: t('admin.audio.tabCoverage'), children: (
            <Table rowKey="presetId" loading={coverageLoading} dataSource={coverage} columns={coverageColumns}
                   pagination={false} scroll={{ x: 900 }}
                   expandable={{
                       onExpand: (expanded, row) => { if (expanded) loadProblems(row); },
                       expandedRowRender: (row) => {
                           const found = problems[row.presetId];
                           if (!found) {
                               return <Text type="secondary">…</Text>;
                           }
                           return (
                               <Space direction="vertical">
                                   {['failed', 'missing', 'stale'].map((kind) => (
                                       <div key={kind}>
                                           <Text strong>{kind}: </Text>
                                           <Text>{(found[kind] || []).length === 0 ? '—' : found[kind].join(', ')}</Text>
                                       </div>
                                   ))}
                               </Space>
                           );
                       },
                   }} />
        ) },
        { key: 'jobs', label: t('admin.audio.tabJobs'), children: (
            <Space direction="vertical" style={{ width: '100%' }}>
                <Button icon={<ReloadOutlined />} onClick={loadJobs}>{t('admin.audio.refresh')}</Button>
                <Table rowKey="id" loading={jobsLoading} dataSource={jobs} columns={jobColumns}
                       pagination={{ pageSize: 20 }} scroll={{ x: 900 }}
                       expandable={{
                           rowExpandable: (job) => (jobProgress(job).failed > 0),
                           onExpand: (expanded, job) => { if (expanded) loadJobDetails(job); },
                           expandedRowRender: (job) => {
                               const details = jobDetails[job.id];
                               if (!details) {
                                   return <Text type="secondary">…</Text>;
                               }
                               return (
                                   <ul>{(details.failedItems || []).map((item) => (
                                       <li key={`${item.kind}-${item.subjectId}`}>{item.headword}: {item.text} — {item.lastError}</li>))}</ul>
                               );
                           },
                       }} />
            </Space>
        ) },
        { key: 'new', label: t('admin.audio.tabNewJob'), children: (
            <Card>
                <Form form={form} layout="vertical" initialValues={{ targetType: 'PRESET', scope: ['WORD'], trigger: 'NOW', priority: 0 }}>
                    <Form.Item name="name" label={t('admin.audio.jobName')}><Input /></Form.Item>
                    <Form.Item name="targetType" label={t('admin.audio.target')} rules={[{ required: true }]}>
                        <Select options={[
                            { value: 'PRESET', label: t('admin.audio.targetPreset') },
                            { value: 'ALL_SYSTEM_PRESETS', label: t('admin.audio.targetAllPresets') },
                            { value: 'WORD_LIST', label: t('admin.audio.targetWordList') },
                        ]} />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, next) => prev.targetType !== next.targetType}>
                        {({ getFieldValue }) => (
                            <>
                                {getFieldValue('targetType') === 'PRESET' && (
                                    <Form.Item name="presetIds" label={t('admin.audio.presets')} rules={[{ required: true }]}>
                                        <Select mode="multiple" options={presetOptions} />
                                    </Form.Item>)}
                                {getFieldValue('targetType') === 'WORD_LIST' && (
                                    <>
                                        <Form.Item name="locale" label={t('admin.audio.locale')} rules={[{ required: true }]}>
                                            <Input placeholder="ENGLISH_GB" />
                                        </Form.Item>
                                        <Form.Item name="dictionaryWordIds" label={t('admin.audio.wordIds')} rules={[{ required: true }]}>
                                            <Input.TextArea rows={3} />
                                        </Form.Item>
                                    </>)}
                            </>
                        )}
                    </Form.Item>
                    <Form.Item name="scope" label={t('admin.audio.scope')} rules={[{ required: true }]}>
                        <Checkbox.Group options={KINDS.map((kind) => ({ value: kind, label: t(`admin.audio.kind.${kind}`) }))} />
                    </Form.Item>
                    <Space wrap align="start">
                        <Form.Item name="voiceProvider" label={t('admin.audio.voiceProvider')}>
                            <Select allowClear style={{ width: 160 }} options={[
                                { value: 'AWS', label: 'AWS Polly' }, { value: 'GOOGLE', label: 'Google TTS' }]} />
                        </Form.Item>
                        <Form.Item name="voiceEngine" label={t('admin.audio.voiceEngine')}><Input placeholder="neural2" style={{ width: 140 }} /></Form.Item>
                        <Form.Item name="voiceName" label={t('admin.audio.voiceName')}><Input placeholder="en-GB-Neural2-A" style={{ width: 200 }} /></Form.Item>
                        <Form.Item name="voiceLanguage" label={t('admin.audio.voiceLanguage')}><Input placeholder="en-GB" style={{ width: 120 }} /></Form.Item>
                    </Space>
                    <Space wrap align="start">
                        <Form.Item name="priority" label={t('admin.audio.priority')}><InputNumber /></Form.Item>
                        <Form.Item name="trigger" label={t('admin.audio.trigger')}>
                            <Select style={{ width: 220 }} options={[
                                { value: 'NOW', label: t('admin.audio.triggerNow') },
                                { value: 'AT_PERIOD_END', label: t('admin.audio.triggerPeriodEnd') },
                                { value: 'CRON', label: t('admin.audio.triggerCron') }]} />
                        </Form.Item>
                        <Form.Item name="cron" label="cron"><Input placeholder="0 0 3 * * *" style={{ width: 160 }} /></Form.Item>
                        <Form.Item name="budgetVoiceClass" label={t('admin.audio.budgetClass')}><Input placeholder="neural2" style={{ width: 140 }} /></Form.Item>
                        <Form.Item name="budgetMaxChars" label={t('admin.audio.budgetChars')}><InputNumber min={0} style={{ width: 160 }} /></Form.Item>
                    </Space>
                    {estimate && (
                        <Alert type="info" showIcon style={{ marginBottom: 16 }}
                               message={t('admin.audio.estimate', { items: estimate.items, chars: estimate.chars, ready: estimate.alreadyReady })}
                               description={Object.entries(estimate.charsByVoiceClass || {}).map(([cls, chars]) => (
                                   <div key={cls}>{cls}: {chars.toLocaleString()} / {t('admin.audio.freeLeft')} {(estimate.remainingFreeChars?.[cls] ?? 0).toLocaleString()}</div>))} />
                    )}
                    <Space>
                        <Button onClick={runEstimate}>{t('admin.audio.estimateButton')}</Button>
                        <Button type="primary" onClick={submitJob}>{t('admin.audio.createJob')}</Button>
                    </Space>
                </Form>
            </Card>
        ) },
        { key: 'usage', label: t('admin.audio.tabUsage'), children: (
            <Space direction="vertical" style={{ width: '100%' }}>
                <Table rowKey={(row) => `${row.provider}-${row.voiceClass}-${row.period}`} dataSource={usage}
                       columns={usageColumns} pagination={false} />
                <Card size="small" title={t('admin.audio.freeLimits')}>
                    {limitRows.length === 0 ? <Text type="secondary">—</Text> : limitRows.map((row) => (
                        <div key={`${row.provider}-${row.voiceClass}`}>{row.provider} / {row.voiceClass}: {row.free.toLocaleString()}</div>))}
                </Card>
            </Space>
        ) },
    ];

    return (
        <div style={{ padding: 16 }}>
            <Typography.Title level={3}>{t('admin.audio.title')}</Typography.Title>
            <Tabs items={tabs} />
        </div>
    );
};

export default AdminAudioPage;
